import { useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import {
  useFreshdeskEmailIntegrationAutoSetupMutation,
  useGetFreshdeskEmailAgentsQuery,
  useGetFreshdeskEmailGroupsQuery,
  useGetProjectByIdQuery,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { useAppSelector } from 'src/store';
import { ProductsTypeBack } from 'src/constants/products';
import { LoaderWrapper } from 'src/components/LoaderWrapper/LoaderWrapper';
import { Skeleton } from 'src/components/ui/skeleton';
import { useForm } from 'react-hook-form';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { yupResolver } from '@hookform/resolvers/yup';
import { object } from 'yup';
import { stringScheme } from 'src/utils/validation-schemes';
import { AgentsSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/AgentsSelect/AgentsSelect';
import { EscalationGroupSelect } from 'src/components/Integrations/IntegrationForms/IntegrationFormFields/EscalationGroupSelect/EscalationGroupSelect';

export const FreshdeskEmailStep3 = ({
  onClick = (_data?: any) => {},
  onClose = (_v?: any) => {},
  setNextButtonDisabled = (_v?: any) => {},
}: {
  onClick?: (data?: any) => void;
  onClose?: (v?: any) => void;
  setNextButtonDisabled?: (v?: any) => void;
}) => {
  const { id } = useParams();
  const loader = useLoader();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id }) as any;
  const { selectedIntegration } = useAppSelector((s) => s.integrationSlices);
  const [agents, setAgents] = useState([]);
  const [groups, setGroups] = useState([]);
  const {
    data: agentsResponse,
    isLoading: isLoadingAgents,
    isError: isErrorAgents,
  } = useGetFreshdeskEmailAgentsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      type: ProductsTypeBack[currentProject?.project_type],
    },
    { skip: !selectedIntegration || !selectedIntegration.id || !currentProject },
  ) as any;

  const {
    data: groupsResponse,
    isLoading: isLoadingGroups,
    isError: isErrorGroups,
  } = useGetFreshdeskEmailGroupsQuery(
    {
      projectId: id,
      integrationId: selectedIntegration?.id,
      type: ProductsTypeBack[currentProject?.project_type],
    },
    { skip: !selectedIntegration || !selectedIntegration.id || !currentProject },
  ) as any;

  const [
    setFreshdeskAutoSetup,
    { isLoading: isLoadingSetFreshdeskAutoSetup, isSuccess: isSuccessSetFreshdeskAutoSetup },
  ] = useFreshdeskEmailIntegrationAutoSetupMutation();

  useEffect(() => {
    if (agentsResponse) {
      setAgents([...agentsResponse]);
    }
    if (groupsResponse) {
      setGroups([...groupsResponse]);
    }
  }, [agentsResponse, groupsResponse]);

  const { handleSubmit, control, setValue } = useForm<any>({
    defaultValues: {
      responder_id: '',
      responder_email: '',
      escalate_assign_group_id: '',
    },
    resolver: yupResolver(
      object({
        responder_id: stringScheme({ required: true }).label('Responder Id'),
        escalate_assign_group_id: stringScheme({ required: false }).label('Escalate As'),
      }),
    ),
  });

  useEffect(() => {
    if (agentsResponse) {
      setAgents([...agentsResponse]);
    }
    if (groupsResponse) {
      setGroups([...groupsResponse]);
    }
  }, [agentsResponse, groupsResponse]);

  useEffect(() => {
    if (isLoadingAgents || isLoadingGroups || isLoadingSetFreshdeskAutoSetup) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isLoadingAgents, isLoadingGroups, isLoadingSetFreshdeskAutoSetup]);

  useEffect(() => {
    if (isSuccessSetFreshdeskAutoSetup) {
      onClick({
        ...selectedIntegration,
        integration_data: {
          ...selectedIntegration.integration_data,
          is_setup: true,
        },
      });
    }
  }, [isSuccessSetFreshdeskAutoSetup]);

  useEffect(() => {
    if (isErrorAgents) {
      setNextButtonDisabled(true);
    }
  }, [isErrorAgents]);

  const submit = async (data) => {
    const request: { projectId: any; integrationId: any; body: Record<string, any> } = {
      projectId: id,
      integrationId: selectedIntegration.id,
      body: {
        responder_id: +data.responder_id,
        responder_email: data.responder_email,
      },
    };

    if (data.escalate_assign_group_id) {
      request.body.escalate_assign_group_id = +data.escalate_assign_group_id;
    }

    await setFreshdeskAutoSetup(request);
  };

  return (
    <form id="freshdesk-step-3-form" onSubmit={handleSubmit(submit)}>
      <div className="content">
        <LoaderWrapper
          loader={<Skeleton className="w-full h-16" />}
          isLoading={isLoadingAgents || isLoadingGroups || isLoadingSetFreshdeskAutoSetup}
        >
          {!isErrorAgents ? (
            <div className={'flex flex-col gap-2.5'}>
              {agents.length > 0 && (
                <AgentsSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Freshdesk account for AI{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: <>AI will use this account to handle tickets.</>,
                        }}
                      />
                    </div>
                  }
                  control={control}
                  setValue={setValue}
                  name={'responder_id'}
                  options={agents}
                />
              )}
              {groups.length > 0 && (
                <EscalationGroupSelect
                  label={
                    <div className={'flex flex-row gap-2 items-center mb-1'}>
                      Escalation group{' '}
                      <InputInfoToolTip
                        infoToolTip={{
                          side: 'right',
                          description: <>AI will assign escalated tickets to this group.</>,
                        }}
                      />
                    </div>
                  }
                  control={control}
                  name={'escalate_assign_group_id'}
                  options={groups}
                />
              )}
            </div>
          ) : (
            <p>
              The Freshdesk details you entered seem invalid. Please double-check your subdomain,
              and API token following{' '}
              <a href={selectedIntegration.setupGuideUrl} target="_blank" rel="noreferrer">
                this guide
              </a>
              .
            </p>
          )}
        </LoaderWrapper>
      </div>
    </form>
  );
};
